'use client';

import React, { isValidElement, useContext, type FC, type ReactNode } from 'react';
import type { ListBoxItemProps as AriaListBoxItemProps } from 'react-aria-components';
import { ListBoxItem as AriaListBoxItem, Text as AriaText } from 'react-aria-components';
import { CheckIcon } from '@milanote/roma-icons';
import { cx } from '../../../utils/cx';
import { SelectContext, type SelectItemType } from './Select';
import { styles, type SelectSize } from './SelectField';
import { isReactComponent } from '../../../utils/isReactComponent';
import type { AvatarProps } from '../avatar';
import { Avatar } from '../avatar';

const SEMANTIC_NAME_BASE = 'RomaSelectItemBase';
const SEMANTIC_NAME = 'RomaSelectItem';

export type SelectItemBaseProps = {
    label?: string;
    avatar?: AvatarProps;
    supportingText?: string;
    icon?: FC | ReactNode;
    size?: SelectSize;
    isSelected?: boolean;
    isDisabled?: boolean;
    isFocused?: boolean;
    isFocusVisible?: boolean;
    children?: ReactNode;
    className?: string;
};

export const SelectItemBase = ({
    label,
    avatar,
    supportingText,
    icon: Icon,
    size: sizeProp,
    isSelected,
    isDisabled,
    isFocused,
    isFocusVisible,
    children,
    className,
}: SelectItemBaseProps) => {
    const { size: contextSize } = useContext(SelectContext);
    const size = sizeProp ?? contextSize;

    return (
        <div
            data-in-select-item
            className={cx(
                SEMANTIC_NAME_BASE,
                'flex cursor-pointer items-center gap-2 rounded-xs outline-hidden select-none text-primary',
                isSelected && 'bg-active',
                isDisabled && 'cursor-not-allowed',
                isFocused && 'bg-primary_hover',
                isFocusVisible && 'ring-2 ring-focus-ring ring-inset',

                // Icon styles
                styles.sizes[size].itemIcon,
                isDisabled && '*:data-icon:text-fg-disabled',

                // Size styles
                '*:data-in-select-item',

                styles.sizes[size].item,
                className,
            )}
        >
            {avatar && (label || supportingText) ? (
                <Avatar.LabelGroup
                    {...avatar}
                    size={size}
                    title={label || children}
                    subtitle={supportingText || ''}
                    className="-my-1"
                />
            ) : isReactComponent(Icon) ? (
                <Icon data-icon aria-hidden="true" />
            ) : isValidElement(Icon) ? (
                Icon
            ) : null}

            {!avatar && (
                <div className="flex w-full min-w-0 flex-1 flex-wrap gap-x-xs">
                    <AriaText
                        slot="label"
                        className={cx('truncate font-medium whitespace-nowrap', isDisabled && 'text-disabled')}
                    >
                        {label || children}
                    </AriaText>

                    {supportingText && (
                        <AriaText
                            slot="description"
                            className={cx('whitespace-nowrap truncate text-tertiary', isDisabled && 'text-disabled')}
                        >
                            {supportingText}
                        </AriaText>
                    )}
                </div>
            )}

            {isSelected && (
                <CheckIcon
                    aria-hidden="true"
                    className={cx(
                        'ml-auto text-fg-primary',
                        styles.sizes[size].check,
                        isDisabled && 'text-fg-disabled',
                    )}
                />
            )}
        </div>
    );
};

interface SelectItemProps extends Omit<AriaListBoxItemProps<SelectItemType>, 'id'>, SelectItemType {}

export const SelectItem = ({
    label,
    id,
    value,
    avatar,
    supportingText,
    isDisabled,
    icon,
    className,
    children,
    ...props
}: SelectItemProps) => {
    const labelOrChildren = label || (typeof children === 'string' ? children : '');
    const textValue = supportingText ? labelOrChildren + ' ' + supportingText : labelOrChildren;

    return (
        <AriaListBoxItem
            id={id}
            value={
                value ?? {
                    id,
                    label: labelOrChildren,
                    avatar,
                    supportingText,
                    isDisabled,
                    icon,
                }
            }
            textValue={textValue}
            isDisabled={isDisabled}
            {...props}
            className={(state) =>
                cx('w-full px-1.5 py-px outline-hidden', typeof className === 'function' ? className(state) : className)
            }
        >
            {(state) => (
                <SelectItemBase
                    label={label}
                    avatar={avatar}
                    supportingText={supportingText}
                    icon={icon}
                    isSelected={state.isSelected}
                    isDisabled={state.isDisabled}
                    isFocused={state.isFocused}
                    isFocusVisible={state.isFocusVisible}
                >
                    {typeof children === 'function' ? children(state) : children}
                </SelectItemBase>
            )}
        </AriaListBoxItem>
    );
};

SelectItemBase.displayName = SEMANTIC_NAME_BASE;
SelectItem.displayName = SEMANTIC_NAME;
